/**
 * Custom event system for clearing active encounter across app boundaries
 *
 * This allows non-React code (like RTK Query mutations) to trigger React Context updates
 * for the active encounter ID, ensuring both localStorage and React state stay in sync.
 */

export const CLEAR_ACTIVE_ENCOUNTER_EVENT = 'app:clear-active-encounter';

export interface ClearActiveEncounterDetail {
  /** Reason for clearing the active encounter */
  reason: 'appointment-deleted' | 'series-deleted' | 'manual';
  /** The encounter ID that was cleared (optional, for logging/debugging) */
  encounterId?: string;
}

/**
 * Dispatches a custom event to clear the active encounter
 *
 * This ensures both localStorage AND React state are synchronized when clearing
 * the active encounter from code that cannot access React Context (e.g., RTK Query).
 *
 * @param detail - Details about why the encounter is being cleared
 *
 * @example
 * // From RTK Query mutation
 * dispatchClearActiveEncounter({
 *   reason: 'appointment-deleted',
 *   encounterId: 'encounter-123',
 * });
 */
export function dispatchClearActiveEncounter(
  detail: ClearActiveEncounterDetail = { reason: 'manual' },
): void {
  const event = new CustomEvent<ClearActiveEncounterDetail>(CLEAR_ACTIVE_ENCOUNTER_EVENT, {
    detail,
  });
  window.dispatchEvent(event);
}
